import { format } from 'date-fns'
import { useSearchParams } from 'react-router'

import { DateInput } from '@/components/ui/DateInput'
import { useSalesAnalytics } from '@/models/salesAnalytics'
import { LeadSource } from '@/models/salesLead'
import { useStaffUsers } from '@/models/user'
import { RepSelect, SourceSelect } from '../SalesLeads/LeadFilters'
import { ActivityCard } from './ActivityCard'
import { ClosedWonByRepCard } from './ClosedWonByRepCard'
import { ClosedWonCard } from './ClosedWonCard'
import { ClosedWonDetailCard } from './ClosedWonDetailCard'
import { FunnelCard } from './FunnelCard'
import { PremiumBreakdownCard } from './PremiumBreakdownCard'
import { PremiumByLineCard } from './PremiumByLineCard'
import { SalesAnalyticsSkeleton } from './SalesAnalyticsSkeleton'
import { ActivityTrendCard, FunnelTrendCard } from './TrendCards'

const toParam = (date: Date | undefined) => (date ? format(date, 'yyyy-MM-dd') : '')

export function SalesAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { staffUsers } = useStaffUsers()

  const start = searchParams.get('start') || ''
  const end = searchParams.get('end') || ''
  const rep = searchParams.get('rep') || ''
  const source = searchParams.get('source') || ''

  function updateParams(updates: Record<string, string | undefined>) {
    const params: Record<string, string> = {}
    for (const [key, value] of searchParams.entries()) params[key] = value
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === undefined) delete params[key]
      else params[key] = value
    })
    setSearchParams(params)
  }

  const { summary, isLoading } = useSalesAnalytics({
    start: start || undefined,
    end: end || undefined,
    rep: rep || undefined,
    source: source ? (source as LeadSource) : undefined,
  })

  const inboundStart = summary?.inboundTrackingStart
  const seriesLabel =
    source === LeadSource.INBOUND ? 'Inbound' : source === LeadSource.OUTBOUND ? 'Outbound' : 'All sources'

  return (
    <div className="flex flex-col gap-6 py-6 md:p-6">
      <div>
        <h1 className="text-xl font-medium">Sales analytics</h1>
        <p className="text-sm text-neutral-500">
          Pipeline, closed won and rep activity. Internal accounts are excluded, and a customer split across
          business entities counts once.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <DateInput
          label="From"
          value={start ? new Date(`${start}T00:00:00`) : undefined}
          onChange={(date) => updateParams({ start: toParam(date) })}
        />
        <DateInput
          label="To"
          value={end ? new Date(`${end}T00:00:00`) : undefined}
          onChange={(date) => updateParams({ end: toParam(date) })}
        />
        <RepSelect
          value={rep}
          staffUsers={staffUsers}
          onChange={(value) => updateParams({ rep: value })}
          className="w-full sm:w-40"
        />
        <SourceSelect
          value={source}
          onChange={(value) => updateParams({ source: value })}
          className="w-full sm:w-40"
        />
      </div>

      {source === LeadSource.INBOUND && inboundStart !== undefined && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          Inbound means the lead carries the web-dot-entry tag — they entered their DOT on the website, even
          if we had called them first — or they called, emailed or texted us before we had touched them. A
          customer whose DOT was only looked up after they bought stays outbound. The web funnel went live on{' '}
          {inboundStart}, so website-sourced leads start there; carriers phoning in predate it.
        </div>
      )}

      {isLoading && <SalesAnalyticsSkeleton />}

      {summary && (
        <div className="flex flex-col gap-6">
          <FunnelCard stages={summary.funnel} applicationsWithoutLead={summary.applicationsWithoutLead} />
          <FunnelTrendCard months={summary.monthlyTrend} seriesLabel={seriesLabel} />
          <ActivityTrendCard months={summary.monthlyTrend} seriesLabel={seriesLabel} />
          <div className="grid gap-6 lg:grid-cols-2">
            <ClosedWonCard
              title="Closed won by bound date"
              description="When we won the deal."
              months={summary.closedWonByBoundDate}
              totals={summary.closedWonByBoundDateTotals}
            />
            <ClosedWonCard
              title="Closed won by policy start"
              description="When the premium starts earning."
              months={summary.closedWonByPolicyStart}
              totals={summary.closedWonByPolicyStartTotals}
            />
          </div>
          <ClosedWonByRepCard rows={summary.closedWonByRep} />
          <ActivityCard rows={summary.activityByRep} />
          <ClosedWonDetailCard rows={summary.closedWonByAccount} />
          <PremiumByLineCard lines={summary.premiumByLine} totals={summary.premiumBreakdownTotals} />
          <PremiumBreakdownCard accounts={summary.closedWonByAccount} />
          {summary.excludedInternalOrgs.length > 0 && (
            <details className="text-sm text-neutral-500">
              <summary className="cursor-pointer">
                {summary.excludedInternalOrgs.length} internal orgs excluded from every figure above
              </summary>
              <ul className="mt-2 list-disc pl-5">
                {summary.excludedInternalOrgs.map((name) => (
                  <li key={name}>{name}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  )
}
